// A country cookbook on the path: sections of recipes, each ticked once photo-logged.
import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { COUNTRY_COOKBOOKS, CONTENT_RECIPES } from '../../content';
import { useNav, type DetailParams } from '../navigation';
import { useDiet, useHeader } from '../hooks';
import { useProgress } from '../store/progress';
import { useSettings } from '../store/settings';
import { cookbookProgress } from '../logic/streak';
import Icon from '../components/Icon';
import Photo from '../components/Photo';
import { Body, Button, Caption, EmptyState, IconCount, ListItem, ProgressBar, Screen, Section } from '../components/ui';
import { useLabels } from '../labels';
import { font, spacing, usePalette } from '../theme';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

export default function CountryCookbookScreen({ route }: NativeStackScreenProps<DetailParams, 'CountryCookbook'>) {
  // The page shows the flag and the title once; the header stays empty instead of repeating it.
  useHeader({ title: '' }, []);
  return <Screen><CookbookPath bookId={route.params.id} /></Screen>;
}

/** A country cookbook's path: flag, progress and its recipes by section. Also the home screen's body. */
export function CookbookPath({ bookId }: { bookId: string }) {
  const { t } = useTranslation();
  const labels = useLabels();
  const nav = useNav();
  const p = usePalette();
  const book = COUNTRY_COOKBOOKS.find(c => c.id === bookId);
  const log = useProgress(s => s.log);
  const isSuper = useSettings(s => s.isSuper);
  const diet = useDiet();
  const cooked = useMemo(() => new Set(log.map(e => e.recipeId).filter(Boolean)), [log]);
  if (!book) return <EmptyState icon="country" title={t('common.empty')} />;
  const locked = book.premium && !isSuper;
  // Restricted diets remove recipes entirely, so the counter shrinks with them ("x of 6" instead of 8).
  const sections = book.sections
    .map(s => ({ ...s, recipeIds: s.recipeIds.filter(id => CONTENT_RECIPES[id] && !diet.hidden(CONTENT_RECIPES[id])) }))
    .filter(s => s.recipeIds.length);
  const progress = cookbookProgress(sections.flatMap(s => s.recipeIds), log);
  const open = (id: string) => (locked ? nav.navigate('Super') : nav.navigate('Recipe', { id }));

  return (
    <>
      <View style={{ alignItems: 'center', gap: spacing.s }}>
        <Text style={{ fontSize: 64 }} accessible={false}>{book.flag}</Text>
        <Text accessibilityRole="header" style={{ fontSize: font.title, fontWeight: '800', color: p.text, textAlign: 'center' }}>{book.title}</Text>
        {labels ? <Body secondary style={{ textAlign: 'center' }}>{book.explainer}</Body> : null}
      </View>
      <View style={{ gap: spacing.s }}>
        <ProgressBar value={progress.total ? progress.cooked / progress.total : 0} />
        <IconCount icon="checkCircle" count={progress.cooked} total={progress.total} text={t('path.cooked', progress)} />
      </View>
      {locked ? (
        <View style={{ gap: spacing.m, alignItems: 'center' }}>
          <Icon name="lock" size={32} color={p.super} />
          {labels ? <Body style={{ textAlign: 'center' }}>{t('path.superLocked')}</Body> : null}
          <Button label={t('super.title')} icon="super" variant="super" onPress={() => nav.navigate('Super')} iconOnly />
        </View>
      ) : null}
      {sections.map(section => (
        <Section key={section.title} title={section.title}>
          {section.note ? <Caption>{section.note}</Caption> : null}
          {section.recipeIds.map(id => {
            const r = CONTENT_RECIPES[id];
            if (!r) return null;
            const done = cooked.has(id);
            return (
              <ListItem
                key={id}
                title={r.title}
                subtitle={labels ? `${t('common.minutes', { count: r.durationMin })} · ${done ? t('path.cookedBadge') : t('path.notYet')}` : t('common.minutes', { count: r.durationMin })}
                diets={diet.issues(r)}
                leading={<Photo seed={id} style={{ width: 48, height: 48 }} iconSize={22} />}
                right={locked ? <Icon name="lock" color={p.secondaryText} /> : <Icon name={done ? 'checkCircle' : 'circle'} color={done ? p.success : p.secondaryText} />}
                onPress={() => open(id)}
              />
            );
          })}
        </Section>
      ))}
    </>
  );
}
